/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useRef, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import PropTypes from 'prop-types';

import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react';
// Others
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors';
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
// Icons
import {ChevronDownIcon, FilterIcon} from '@salesforce/retail-react-app/app/components/icons';
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner';
// Project Components
import Pagination from '@salesforce/retail-react-app/app/components/pagination';
import ProductTile, {Skeleton as ProductTileSkeleton} from '@salesforce/retail-react-app/app/components/product-tile';
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive';
// Components
import {
  Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader,
  DrawerOverlay, Flex, FormControl, Grid, Heading, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid,
  Stack, Text, useDisclosure,
} from '@salesforce/retail-react-app/app/components/shared/ui';
// Constants
import {
  API_ERROR_MESSAGE, DEFAULT_LIMIT_VALUES, MAX_CACHE_AGE, PRODUCT_LIST_IMAGE_VIEW_TYPE, PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID,
  STALE_WHILE_REVALIDATE, TOAST_ACTION_VIEW_WISHLIST, TOAST_MESSAGE_ADDED_TO_WISHLIST, TOAST_MESSAGE_REMOVED_FROM_WISHLIST,
} from '@salesforce/retail-react-app/app/constants';
// Hooks
import {useLimitUrls, usePageUrls, useSearchParams, useSortUrls} from '@salesforce/retail-react-app/app/hooks';
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data';
import useDataCloud from '@salesforce/retail-react-app/app/hooks/use-datacloud';
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list';
import AbovePageHeader from '@salesforce/retail-react-app/app/pages/product-list/partials/above-page-header';
import CategoryLinks from '@salesforce/retail-react-app/app/pages/product-list/partials/category-links';
import EmptySearchResults from '@salesforce/retail-react-app/app/pages/product-list/partials/empty-results';
import PageDesignerPromotionalBanner
  from '@salesforce/retail-react-app/app/pages/product-list/partials/page-designer-promotional-banner';
import PageHeader from '@salesforce/retail-react-app/app/pages/product-list/partials/page-header';
import SearchPageHeader from '@amblique/king-living/app/pages/product-list/partials/search-page-header';
import Refinements from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements';
import SelectedRefinements from '@salesforce/retail-react-app/app/pages/product-list/partials/selected-refinements';
import logger from '@salesforce/retail-react-app/app/utils/logger-instance';
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils';

import LazyLoadGrid from '@amblique/king-living/app/components/product-list/lazy-load-grid';
import LazyLoadSentinel from '@amblique/king-living/app/components/product-list/lazy-load-sentinel';
import RangeProductTile from '@amblique/king-living/app/components/range-product-tile';
import {VIEW_ITEM_LIST, useGa4TileSelect, useGa4ViewItemList} from '@amblique/king-living/app/analytics/ga4';
import {RANGE_PLP_PAGE_SIZE} from '@amblique/king-living/app/constants';
import {useMergeSearchHitShortDescriptions} from '@amblique/king-living/app/hooks/use-merge-search-hit-short-descriptions';
import Seo from '@amblique/king-living/app/components/seo';
import {buildPageJsonLdGraph, SEO_MANAGED_META_NAMES} from '@amblique/king-living/app/utils/seo-utils';

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew'];

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
export function ProductList({
  isLoading,
  isFetched,
  isRefetching,
  productSearchResult,

  error,
  category,

  // KINDW-99 / FSD PLP-03: when `true`, render the Range Detail PLP grid (offset-based lazy load,
  // simplified tile, no filter/sort UI). Default `false` preserves the legacy URL-paginated PLP
  // for `/category/*` routes verbatim.
  lazyLoad = false,
  pageSize = RANGE_PLP_PAGE_SIZE,

  // KINDW-103 / FSD PLP-07: Contentful-driven whitelist of refinement attribute IDs that should be
  // exposed as filter tag chips above the grid. Forwarded to `LazyLoadGrid` only when `lazyLoad`.
  filterTagAttributeIds,

  // Range Detail PLP heading sourced from Contentful `ProductListing - Dynamic` entry.
  // `LazyLoadGrid` falls back to `category.name` when this is empty/whitespace.
  contentfulTitle,

  /** When set (search route), footer uses Range PLP infinite scroll instead of URL pagination. */
  searchLazyPagination,
  categoryId: categoryIdProp,
  ...props
}) {
  // Range Detail PLP: short-circuit before any of the legacy URL-pagination plumbing runs. The
  // lazy grid manages its own data lifecycle via `useLazyProductSearch`.
  if (lazyLoad) {
    return (
      <LazyLoadGrid
        category={category}
        contentfulTitle={contentfulTitle}
        filterTagAttributeIds={filterTagAttributeIds}
        pageSize={pageSize}
        {...props}
      />
    );
  }

  // Using destructuring to omit properties; we must rename `isLoading` because we use a different
  // `isLoading` later in this function.
  // eslint-disable-next-line react/prop-types, no-unused-vars
  const {isLoading: _unusedIsLoading, staticContext, ...rest} = props;
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {formatMessage} = useIntl();
  const navigate = useNavigation();
  const history = useHistory();
  const params = useParams();
  const location = useLocation();
  const toast = useToast();
  const einstein = useEinstein();
  const dataCloud = useDataCloud();
  const activeData = useActiveData();
  const {res} = useServerContext();
  const customerId = useCustomerId();
  const [searchParams, {stringify: stringifySearchParams}] = useSearchParams();

  /** ************** Page State ****************/
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState([]);
  const [sortOpen, setSortOpen] = useState(false);

  const urlParams = new URLSearchParams(location.search);
  let searchQuery = urlParams.get('q');
  const isSearch = !!searchQuery;
  const tileProducts = useMergeSearchHitShortDescriptions(isSearch ? productSearchResult?.hits : undefined);
  const searchHitCount = productSearchResult?.hits?.length ?? 0;
  const isHydratingTiles =
    isSearch && searchHitCount > 0 && tileProducts.length < searchHitCount;
  const extraPageMetaTags = useMemo(
    () => (productSearchResult?.pageMetaTags ?? []).filter(({id}) => !SEO_MANAGED_META_NAMES.has(id)),
    [productSearchResult?.pageMetaTags]
  );
  const appOrigin = useAppOrigin();
  const {locale, site} = useMultiSite();
  const resolvedCategoryId = categoryIdProp ?? params.categoryId ?? category?.id;
  const seoOwnedByParent = Boolean(categoryIdProp && !params.categoryId);
  const jsonLdGraph = useMemo(() => {
    if (seoOwnedByParent) {
      return null;
    }

    return buildPageJsonLdGraph({
      appOrigin,
      category: category || (resolvedCategoryId ? {id: resolvedCategoryId} : undefined),
      currency: locale?.preferredCurrency || 'AUD',
      pathname: location.pathname,
      siteId: site?.id,
    });
  }, [
    category,
    resolvedCategoryId,
    seoOwnedByParent,
    appOrigin,
    location.pathname,
    locale?.preferredCurrency,
    site?.id
  ]);

  if (params.categoryId) {
    searchParams._refine.push(`cgid=${params.categoryId}`);
  }

  /** ************** Mutation Actions ****************/
  const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
    'createCustomerProductListItem'
  );
  const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
    'deleteCustomerProductListItem'
  );

  /** ************** Query Actions ****************/
  // _refine is an invalid param for useProductSearch, we don't want to pass it to API call
  // const {_refine, ...restOfParams} = searchParams;

  // const {
  //   isLoading,
  //   isFetched,
  //   isRefetching,
  //   data: productSearchResult,
  // } = useProductSearch(
  //   {
  //     parameters: {
  //       ...restOfParams,
  //       perPricebook: true,
  //       allVariationProperties: true,
  //       allImages: true,
  //       expand: [
  //         'promotions',
  //         'variations',
  //         'prices',
  //         'images',
  //         'page_meta_tags',
  //         'custom_properties'
  //       ],
  //       refine: _refine,
  //     },
  //   },
  //   {
  //     keepPreviousData: true,
  //   }
  // );

  // const {error, data: category} = useCategory(
  //   {
  //     parameters: {
  //       id: params.categoryId,
  //     },
  //   },
  //   {
  //     enabled: !isSearch && !!params.categoryId,
  //   }
  // );

  // Apply disallow list to refinements.
  if (productSearchResult?.refinements) {
    productSearchResult.refinements = productSearchResult.refinements.filter(
      ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
    );
  }

  /** ************** Error Handling ****************/
  const errorStatus = error?.response?.status;

  switch (errorStatus) {
    case undefined:
      // No Error.
      break;
    case 404:
      throw new HTTPNotFound('Category Not Found.');
    default:
      throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`);
  }

  /** ************** Response Handling ****************/
  if (res) {
    res.set(
      'Cache-Control',
      `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
    );
  }

  // Reset scroll position when `isRefetching` becomes `true` (category PLP filter/sort changes).
  // Search infinite-scroll batches must not jump the viewport back to the top.
  useEffect(() => {
    if (isSearch && searchLazyPagination) {
      setFiltersLoading(isRefetching);

      return;
    }
    isRefetching && window.scrollTo(0, 0);
    setFiltersLoading(isRefetching);
  }, [isRefetching, isSearch, searchLazyPagination]);

  /** ************** Render Variables ****************/
  const basePath = `${location.pathname}${location.search}`;
  const searchHasHits =
    Array.isArray(productSearchResult?.hits) && productSearchResult.hits.length > 0;
  const showNoResults =
    !isLoading && productSearchResult && !searchHasHits;
  const {total, sortingOptions} = productSearchResult || {};
  const selectedSortingOptionLabel =
    sortingOptions?.find(
      (option) => option.id === productSearchResult?.selectedSortingOption
    ) ?? sortingOptions?.[0];

  // Get urls to be used for pagination, page size changes, and sorting.
  const pageUrls = usePageUrls({total});
  const sortUrls = useSortUrls({options: sortingOptions});
  const limitUrls = useLimitUrls();

  const gridSkeletonCount = isSearch ? RANGE_PLP_PAGE_SIZE : searchParams.limit;
  const showFullGridSkeleton =
    isHydrated() &&
    (isSearch ?
      (
        Boolean(!productSearchResult && isLoading) ||
        (isHydratingTiles && tileProducts.length === 0)
      ) :
      Boolean((isRefetching && !isFetched) || !productSearchResult));

  /** ************** Action Handlers ****************/
  const {data: wishlist} = useWishList();
  const addItemToWishlist = async (product) => {
    setWishlistLoading([...wishlistLoading, product.productId]);

    // TODO: This wishlist object is from an old API, we need to replace it with the new one.
    const listId = wishlist.id;

    await createCustomerProductListItem(
      {
        parameters: {customerId, listId},
        body: {
          quantity: 1,
          public: false,
          priority: 1,
          type: 'product',
          productId: product.productId,
        },
      },
      {
        onError: () => {
          toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
          });
        },
        onSuccess: () => {
          toast({
            title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
            status: 'success',
            action: (
              // it would be better if we could use <Button as={Link}>
              // but unfortunately the Link component is not compatible
              // with Chakra Toast, since the ToastManager is rendered via portal
              // and the toast doesn't have access to intl provider, which is a
              // requirement of the Link component.
              <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
              </Button>
            ),
          });
        },
        onSettled: () => {
          setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId));
        },
      }
    );
  };

  const removeItemFromWishlist = async (product) => {
    setWishlistLoading([...wishlistLoading, product.productId]);

    const listId = wishlist.id;
    const itemId = wishlist.customerProductListItems.find(
      (i) => i.productId === product.productId
    ).id;

    await deleteCustomerProductListItem(
      {
        body: {},
        parameters: {customerId, listId, itemId},
      },
      {
        onError: () => {
          toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
          });
        },
        onSuccess: () => {
          toast({
            title: formatMessage(TOAST_MESSAGE_REMOVED_FROM_WISHLIST),
            status: 'success',
          });
        },
        onSettled: () => {
          setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId));
        },
      }
    );
  };

  // Toggles filter on and off
  const toggleFilter = (value, attributeId, selected, allowMultiple = true) => {
    const searchParamsCopy = {...searchParams};

    // Remove the `offset` search param if present.
    delete searchParamsCopy.offset;

    // If we aren't allowing for multiple selections, simply clear any value set for the
    // attribute, and apply a new one if required.
    if (!allowMultiple) {
      const previousValue = searchParamsCopy.refine[attributeId];

      delete searchParamsCopy.refine[attributeId];

      // Note the loose comparison, for "string != number" checks.
      if (!selected && value.value != previousValue) {
        searchParamsCopy.refine[attributeId] = value.value;
      }
    } else {
      // Get the attibute value as an array.
      let attributeValue = searchParamsCopy.refine[attributeId] || [];

      // Ensure that the value is still converted into an array if it's a `string` or `number`.
      if (typeof attributeValue === 'string') {
        attributeValue = attributeValue.split('|');
      } else if (typeof attributeValue === 'number') {
        attributeValue = [attributeValue];
      }

      // Either set the value, or filter the value out.
      if (!selected) {
        attributeValue.push(value.value);
      } else {
        // Note the loose comparison, for "string != number" checks.
        attributeValue = attributeValue?.filter((v) => v != value.value);
      }

      // Update the attribute value in the new search params.
      searchParamsCopy.refine[attributeId] = attributeValue;

      // If the update value is an empty array, remove the current attribute key.
      if (searchParamsCopy.refine[attributeId].length === 0) {
        delete searchParamsCopy.refine[attributeId];
      }
    }

    if (isSearch) {
      navigate(`/search?${stringifySearchParams(searchParamsCopy)}`);
    } else {
      navigate(`/category/${params.categoryId}?${stringifySearchParams(searchParamsCopy)}`);
    }
  };

  // Clears all filters
  const resetFilters = () => {
    const newSearchParams = {
      ...searchParams,
      refine: [],
    };
    const newPath = isSearch ?
      `/search?${stringifySearchParams(newSearchParams)}` :
      `/category/${params.categoryId}?${stringifySearchParams(newSearchParams)}`;

    navigate(newPath);
  };

  /** ************** Einstein ****************/
  useEffect(() => {
    if (productSearchResult) {
      if (isSearch) {
        try {
          einstein.sendViewSearch(searchQuery, productSearchResult);
        } catch (err) {
          logger.error('Einstein sendViewSearch error', {
            namespace: 'ProductList.useEffect',
            additionalProperties: {error: err, searchQuery},
          });
        }
        dataCloud.sendViewSearchResults(searchParams, productSearchResult);
        activeData.sendViewSearch(searchParams, productSearchResult);
      } else {
        try {
          einstein.sendViewCategory(category, productSearchResult);
        } catch (err) {
          logger.error('Einstein sendViewCategory error', {
            namespace: 'ProductList.useEffect',
            additionalProperties: {error: err, category},
          });
        }
        dataCloud.sendViewCategory(searchParams, category, productSearchResult);
        activeData.sendViewCategory(searchParams, category, productSearchResult);
      }
    }
  }, [productSearchResult]);

  /** ************** GA4 view_item_list ****************/
  const ga4ListMeta = isSearch ? VIEW_ITEM_LIST.FULL_SEARCH : VIEW_ITEM_LIST.PLP;
  const {trackNewProducts, resetTracker} = useGa4ViewItemList(ga4ListMeta);
  const handleSearchTileSelect = useGa4TileSelect(
    VIEW_ITEM_LIST.FULL_SEARCH,
    useCallback(
      (product) => {
        try {
          einstein.sendClickSearch(searchQuery, product);
        } catch (err) {
          logger.error('Einstein sendClickSearch error', {
            namespace: 'ProductList.RangeProductTile',
            additionalProperties: {error: err, searchQuery},
          });
        }
      },
      [einstein, searchQuery]
    )
  );
  const prevHitsLengthRef = useRef(0);

  useEffect(() => {
    resetTracker();
    prevHitsLengthRef.current = 0;
  }, [location.search, resetTracker]);

  useEffect(() => {
    const hits = productSearchResult?.hits;

    if (!Array.isArray(hits) || hits.length === 0 || isLoading) {
      return;
    }

    if (isSearch && searchLazyPagination) {
      if (isHydratingTiles && !tileProducts.length) {
        return;
      }

      const prevLength = prevHitsLengthRef.current;
      const batch = tileProducts.slice(prevLength);

      if (!batch.length) {
        return;
      }

      void trackNewProducts(batch, {startIndex: prevLength});
      prevHitsLengthRef.current = tileProducts.length;

      return;
    }

    if (!isSearch) {
      const offset = productSearchResult?.offset ?? 0;

      void trackNewProducts(hits, {startIndex: offset});
    }
  }, [
    productSearchResult,
    isSearch,
    searchLazyPagination,
    isLoading,
    isHydratingTiles,
    tileProducts,
    trackNewProducts
  ]);

  const searchPageHeader = (
    <SearchPageHeader
      isLoading={isLoading}
      isRefetching={isRefetching}
      productSearchResult={productSearchResult}
      searchQuery={searchQuery}
    />
  );

  return (
    <Box
      className="sf-product-list-page"
      data-testid="sf-product-list-page"
      layerStyle="page"
      paddingTop={{base: 6, lg: 8}}
      {...rest}
    >
      {!seoOwnedByParent && (
        <Seo
          description={category?.pageDescription ?? searchQuery}
          jsonLdGraph={jsonLdGraph}
          keywords={category?.pageKeywords}
          title={category?.pageTitle ?? searchQuery}
        >
          {extraPageMetaTags.map(({id, value}) => (
            <meta key={id} content={value} name={id} />
          ))}
        </Seo>
      )}
      {showNoResults ? (
        isSearch ? (
          <>
            <AbovePageHeader />
            <PageDesignerPromotionalBanner />
            <Stack display={{base: 'none', lg: 'flex'}} width="100%">
              {searchPageHeader}
            </Stack>
            <HideOnDesktop>
              <Stack>
                {searchPageHeader}
              </Stack>
            </HideOnDesktop>
          </>
        ) : (
          <EmptySearchResults category={category} searchQuery={searchQuery} />
        )
      ) : (
        <>
          <AbovePageHeader />
          <PageDesignerPromotionalBanner />

          {/* Header — search: inline search + results line only (no filter/sort UI) */}
          {isSearch ? (
            <Stack display={{base: 'none', lg: 'flex'}} width="100%">
              {searchPageHeader}
            </Stack>
          ) : (
            <Stack
              align="flex-start"
              direction="row"
              display={{base: 'none', lg: 'flex'}}
              justify="flex-start"
              marginBottom={6}
              spacing={4}
            >
              <Flex align="left" width="287px">
                <PageHeader
                  category={category}
                  isLoading={isLoading}
                  productSearchResult={productSearchResult}
                />
              </Flex>

              <Box flex={1} paddingTop="45px">
                <SelectedRefinements
                  filters={productSearchResult?.refinements}
                  handleReset={resetFilters}
                  selectedFilterValues={productSearchResult?.selectedRefinements}
                  toggleFilter={toggleFilter}
                />
              </Box>
              <Box paddingTop="45px">
                <Sort
                  basePath={basePath}
                  productSearchResult={productSearchResult}
                  sortUrls={sortUrls}
                />
              </Box>
            </Stack>
          )}

          {/* Filter Button for Mobile */}
          <HideOnDesktop>
            <Stack spacing={6}>
              {isSearch ? (
                searchPageHeader
              ) : (
                <PageHeader
                  category={category}
                  isLoading={isLoading}
                  productSearchResult={productSearchResult}
                />
              )}
              {!isSearch ? (
                <>
                  <Stack
                    align="center"
                    borderColor="gray.100"
                    direction="row"
                    display={{base: 'flex', md: 'none'}}
                    height={12}
                    justify="flex-start"
                    spacing={1}
                  >
                    <Flex align="center">
                      <Button
                        colorScheme="black"
                        display="inline-flex"
                        fontSize="sm"
                        leftIcon={<FilterIcon boxSize={5} />}
                        marginRight={2}
                        variant="outline"
                        onClick={onOpen}
                      >
                        <FormattedMessage
                          defaultMessage="Filter"
                          id="product_list.button.filter"
                        />
                      </Button>
                    </Flex>
                    <Flex align="center">
                      <Button
                        colorScheme="black"
                        display="inline-flex"
                        fontSize="sm"
                        marginRight={2}
                        maxWidth="245px"
                        rightIcon={<ChevronDownIcon boxSize={5} />}
                        variant="outline"
                        onClick={() => setSortOpen(true)}
                      >
                        {formatMessage(
                          {
                            id: 'product_list.button.sort_by',
                            defaultMessage: 'Sort By: {sortOption}',
                          },
                          {
                            sortOption: selectedSortingOptionLabel?.label,
                          }
                        )}
                      </Button>
                    </Flex>
                  </Stack>
                  <Box marginBottom={4}>
                    <SelectedRefinements
                      filters={productSearchResult?.refinements}
                      handleReset={resetFilters}
                      selectedFilterValues={productSearchResult?.selectedRefinements}
                      toggleFilter={toggleFilter}
                    />
                  </Box>
                </>
              ) : null}
            </Stack>
          </HideOnDesktop>

          {/* Body — category PLP keeps filter column; search is full-width grid only */}
          <Grid
            className={isSearch ? 'sf-product-list-page-search-section' : undefined}
            columnGap={6}
            templateColumns={
              isSearch ?
                {base: '1fr', md: '1fr'} :
                {base: '1fr', md: '280px 1fr'}
            }
          >
            {!isSearch ? (
              <Stack display={{base: 'none', md: 'flex'}}>
                <Refinements
                  excludedFilters={['cgid']}
                  filters={productSearchResult?.refinements}
                  isLoading={filtersLoading}
                  selectedFilters={searchParams.refine}
                  toggleFilter={toggleFilter}
                  itemsBefore={
                    category?.categories ?
                      [<CategoryLinks key="itemsBefore" category={category} />] :
                      undefined
                  }
                />
              </Stack>
            ) : null}
            <Box className={isSearch ? 'sf-product-list-page-search-list' : undefined}>
              {/* Search: same grid + tile as Range PLP (`LazyLoadGrid`). Category: legacy ProductTile. */}
              <SimpleGrid
                className={isSearch ? 'sf-product-list-page-search-grid' : undefined}
                columns={isSearch ? [2, 2, 3, 4] : [2, 2, 3, 3]}
                spacingX={isSearch ? undefined : 4}
                spacingY={isSearch ? undefined : {base: 12, lg: 16}}
              >
                {showFullGridSkeleton ?
                  new Array(gridSkeletonCount)
                    .fill(0)
                    .map((value, index) => (
                      <ProductTileSkeleton key={index} />
                    )) :
                  (isSearch ? tileProducts : productSearchResult?.hits)?.map((productSearchItem, index) => {
                    if (isSearch) {
                      return (
                        <RangeProductTile
                          key={productSearchItem.productId}
                          product={productSearchItem}
                          tileIndex={index}
                          variant="searchResults"
                          showPromoCallout
                          onClick={(product) => handleSearchTileSelect(product, index)}
                        />
                      );
                    }

                    const productId = productSearchItem.productId;
                    const isInWishlist =
                      !!wishlist?.customerProductListItems?.find(
                        (item) => item.productId === productId
                      );

                    return (
                      <ProductTile
                        key={productSearchItem.productId}
                        data-testid={`sf-product-tile-${productSearchItem.productId}`}
                        enableFavourite={true}
                        imageViewType={PRODUCT_LIST_IMAGE_VIEW_TYPE}
                        isFavourite={isInWishlist}
                        isRefreshingData={isRefetching && isFetched}
                        product={productSearchItem}
                        dynamicImageProps={{
                          widths: [
                            '50vw',
                            '50vw',
                            '20vw',
                            '20vw',
                            '25vw'
                          ],
                        }}
                        selectableAttributeId={
                          PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID
                        }
                        onClick={() => {
                          if (category) {
                            einstein.sendClickCategory(
                              category,
                              productSearchItem
                            );
                          }
                        }}
                        onFavouriteToggle={(toBeFavourite) => {
                          const action = toBeFavourite ?
                            addItemToWishlist :
                            removeItemFromWishlist;

                          return action(productSearchItem);
                        }}
                      />
                    );
                  })}
                {((isSearch && searchLazyPagination?.isLoadingMore) ||
                  (isSearch && isHydratingTiles && tileProducts.length < searchHitCount)) &&
                  new Array(
                    isSearch && searchLazyPagination?.isLoadingMore ?
                      Math.min(
                        searchLazyPagination.pageSize,
                        Math.max(
                          (productSearchResult?.total ?? 0) - searchHitCount,
                          0
                        )
                      ) :
                      searchHitCount - tileProducts.length
                  )
                    .fill(0)
                    .map((_, i) => (
                      <ProductTileSkeleton key={`search-load-more-skel-${i}`} />
                    ))}
              </SimpleGrid>
              {/* Footer — search: Range PLP sentinel + end-of-results copy; category: pagination */}
              {isSearch && searchLazyPagination ? (
                <Flex className="sf-product-list-page-search-footer">
                  {searchLazyPagination.hasMore ? (
                    <LazyLoadSentinel
                      enabled={
                        searchLazyPagination.hasMore && !searchLazyPagination.isLoadingMore
                      }
                      onIntersect={searchLazyPagination.loadMore}
                    />
                  ) : (
                    (productSearchResult?.hits?.length ?? 0) > 0 && (
                      <Text
                        aria-live="polite"
                        data-testid="search-product-list-end"
                        aria-label={formatMessage(
                          {
                            defaultMessage: 'Showing {count} of {total} products.',
                            id: 'range_product_list.live.showing',
                          },
                          {
                            count: productSearchResult?.hits?.length,
                            total: productSearchResult?.total,
                          }
                        )}
                      >
                        <FormattedMessage
                          defaultMessage="Showing all {total} products"
                          id="range_product_list.end_of_results"
                          values={{total: productSearchResult?.total}}
                        />
                      </Text>
                    )
                  )}
                </Flex>
              ) : (
                <Flex
                  justifyContent={['center', 'center', 'flex-start']}
                  paddingTop={8}
                >
                  <Pagination currentURL={basePath} urls={pageUrls} />

                  {/*
                            Our design doesn't call for a page size select. Show this element if you want
                            to add one to your design.
                        */}
                  <Select
                    display="none"
                    value={basePath}
                    onChange={({target}) => {
                      history.push(target.value);
                    }}
                  >
                    {limitUrls.map((href, index) => (
                      <option key={href} value={href}>
                        {DEFAULT_LIMIT_VALUES[index]}
                      </option>
                    ))}
                  </Select>
                </Flex>
              )}
            </Box>
          </Grid>
        </>
      )}
      {/* Modal for filter options on mobile (category PLP only) */}
      {!isSearch ? (
        <Modal
          isOpen={isOpen}
          motionPreset="slideInBottom"
          scrollBehavior="inside"
          size="full"
          onClose={onClose}
        >
          <ModalOverlay />
          <ModalContent marginTop={0} top={0}>
            <ModalHeader>
              <Heading as="h1" fontSize="2xl" fontWeight="bold">
                <FormattedMessage
                  defaultMessage="Filter"
                  id="product_list.modal.title.filter"
                />
              </Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody py={4}>
              {filtersLoading && <LoadingSpinner />}
              <Refinements
                excludedFilters={['cgid']}
                filters={productSearchResult?.refinements}
                selectedFilters={searchParams.refine}
                toggleFilter={toggleFilter}
                itemsBefore={
                  category?.categories ?
                    [
                      <CategoryLinks
                        key="itemsBefore"
                        category={category}
                        onSelect={onClose}
                      />
                    ] :
                    undefined
                }
              />
            </ModalBody>

            <ModalFooter
            // justify="space-between"
              borderColor="gray.100"
              borderTop="1px solid"
              display="block"
              paddingBottom={10}
              width="full"
            >
              <Stack>
                <Button width="full" onClick={onClose}>
                  {formatMessage(
                    {
                      id: 'product_list.modal.button.view_items',
                      defaultMessage: 'View {prroductCount} items',
                    },
                    {
                      prroductCount: productSearchResult?.total,
                    }
                  )}
                </Button>
                <Button variant="outline" width="full" onClick={resetFilters}>
                  <FormattedMessage
                    defaultMessage="Clear Filters"
                    id="product_list.modal.button.clear_filters"
                  />
                </Button>
              </Stack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ) : null}
      {!isSearch ? (
        <Drawer
          height="50%"
          isFullHeight={false}
          isOpen={sortOpen}
          motionPreset="slideInBottom"
          placement="bottom"
          scrollBehavior="inside"
          size="sm"
          onClose={() => setSortOpen(false)}
        >
          <DrawerOverlay />
          <DrawerContent marginTop={0}>
            <DrawerHeader boxShadow="none">
              <Text fontSize="2xl" fontWeight="bold">
                <FormattedMessage
                  defaultMessage="Sort By"
                  id="product_list.drawer.title.sort_by"
                />
              </Text>
            </DrawerHeader>
            <DrawerCloseButton />
            <DrawerBody>
              {sortUrls.map((href, idx) => (
                <Button
                  key={idx}
                  fontSize="md"
                  marginTop={0}
                  variant="menu-link"
                  width="full"
                  onClick={() => {
                    setSortOpen(false);
                    history.push(href);
                  }}
                >
                  <Text
                    as={
                      selectedSortingOptionLabel?.label ===
                      productSearchResult?.sortingOptions[idx]?.label && 'u'
                    }
                  >
                    {productSearchResult?.sortingOptions[idx]?.label}
                  </Text>
                </Button>
              ))}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      ) : null}
    </Box>
  );
}

ProductList.getTemplateName = () => 'product-list';

ProductList.propTypes = {
  onAddToWishlistClick: PropTypes.func,
  onRemoveWishlistClick: PropTypes.func,
  category: PropTypes.object,
  isLoading: PropTypes.bool,
  isFetched: PropTypes.bool,
  isRefetching: PropTypes.bool,
  productSearchResult: PropTypes.object,
  error: PropTypes.object,
  // KINDW-99 / FSD PLP-03 — Range Detail PLP mode toggle.
  lazyLoad: PropTypes.bool,
  pageSize: PropTypes.number,
  // KINDW-103 / FSD PLP-07 — filter tag whitelist (Contentful-driven).
  filterTagAttributeIds: PropTypes.arrayOf(PropTypes.string),
  // Contentful-authored PLP title (falls back to category name in LazyLoadGrid).
  contentfulTitle: PropTypes.string,
  searchLazyPagination: PropTypes.shape({
    loadMore: PropTypes.func.isRequired,
    hasMore: PropTypes.bool.isRequired,
    isLoadingMore: PropTypes.bool.isRequired,
    pageSize: PropTypes.number.isRequired,
  }),
  categoryId: PropTypes.string,
};

export default ProductList;

const Sort = ({sortUrls, productSearchResult, basePath, ...otherProps}) => {
  const intl = useIntl();
  const history = useHistory();

  return (
    <FormControl
      data-testid="sf-product-list-sort"
      id="page_sort"
      width="auto"
      aria-label={intl.formatMessage({
        id: 'product_list.drawer.title.sort_by',
        defaultMessage: 'Sort By',
      })}
      {...otherProps}
    >
      <Select
        height={11}
        id="sf-product-list-sort-select"
        value={basePath.replace(/(offset)=(\d+)/i, '$1=0')}
        width="240px"
        aria-label={intl.formatMessage({
          id: 'product_list.sort_by.label.assistive_msg',
          defaultMessage: 'Sort products by',
        })}
        onChange={({target}) => {
          history.push(target.value);
        }}
      >
        {sortUrls.map((href, index) => (
          <option key={href} value={href}>
            {intl.formatMessage(
              {
                id: 'product_list.select.sort_by',
                defaultMessage: 'Sort By: {sortOption}',
              },
              {
                sortOption: productSearchResult?.sortingOptions[index]?.label,
              }
            )}
          </option>
        ))}
      </Select>
    </FormControl>
  );
};

Sort.propTypes = {
  sortUrls: PropTypes.array,
  productSearchResult: PropTypes.object,
  basePath: PropTypes.string,
};
